/**
 * Utility functions for working with recipe images
 */

import { recipeImageData, FishRecipeImageData, RecipeImageData } from '../data/recipe_image_data';
import { RECIPE_IMAGE_CONFIG } from '../config/app.config';

/**
 * Get recipe image data for a specific fish
 */
export function getFishRecipeImageData(fishId: string): FishRecipeImageData | undefined {
  return recipeImageData.find(fish => fish.fishId === fishId);
}

/**
 * Get image data for a specific fish and recipe combination
 */
export function getRecipeImageData(fishId: string, recipe: string): RecipeImageData | undefined {
  const fishData = getFishRecipeImageData(fishId);
  if (!fishData) return undefined;
  
  return fishData.recipes.find(r => r.recipe === recipe);
}

/**
 * Get available recipes for a fish (recipes that have images)
 */
export function getAvailableRecipes(fishId: string): string[] {
  const fishData = getFishRecipeImageData(fishId);
  if (!fishData) return [];
  
  return fishData.recipes.map(r => r.recipe);
}

/**
 * Check if a recipe has images for a fish
 */
export function hasImagesForRecipe(fishId: string, recipe: string): boolean {
  const recipeData = getRecipeImageData(fishId, recipe);
  return recipeData !== undefined;
}

/**
 * Get available portion types for a fish and recipe
 */
export function getAvailablePortionTypes(fishId: string, recipe: string): string[] {
  const recipeData = getRecipeImageData(fishId, recipe);
  if (!recipeData) return [];
  
  return Object.keys(recipeData.portionTypes);
}

/**
 * Get available piece types for a fish, recipe, and portion type (when portion type is 'piece')
 */
export function getAvailablePieceTypes(fishId: string, recipe: string): string[] {
  const recipeData = getRecipeImageData(fishId, recipe);
  if (!recipeData || !recipeData.portionTypes.piece) return [];
  
  const pieceData = recipeData.portionTypes.piece;
  return Object.keys(pieceData).filter(key => 
    key !== 'weight_min' && key !== 'weight_max' && key !== 'weights'
  );
}

/**
 * Get the middle weight for a piece type (for displaying preview image)
 */
export function getMiddleWeightForPieceType(fishId: string, recipe: string, pieceType: string): number | undefined {
  const recipeData = getRecipeImageData(fishId, recipe);
  if (!recipeData || !recipeData.portionTypes.piece) return undefined;
  
  const weights = (recipeData.portionTypes.piece as any)[pieceType];
  if (!weights || !Array.isArray(weights) || weights.length === 0) return undefined;
  
  // Return middle weight
  const middleIndex = Math.floor(weights.length / 2);
  return weights[middleIndex];
}

/**
 * Get all weights for a portion type
 */
export function getWeightsForPortionType(
  fishId: string, 
  recipe: string, 
  portionType: 'whole' | 'portion' | 'piece',
  pieceType?: string
): number[] {
  const recipeData = getRecipeImageData(fishId, recipe);
  if (!recipeData) return [];
  
  const portionData = recipeData.portionTypes[portionType];
  if (!portionData) return [];
  
  if (portionType === 'piece' && pieceType) {
    const weights = (portionData as any)[pieceType];
    return Array.isArray(weights) ? weights : [];
  } else {
    return portionData.weights || [];
  }
}

/**
 * Get weight range (min, max) for a portion type
 */
export function getWeightRange(
  fishId: string, 
  recipe: string, 
  portionType: 'whole' | 'portion' | 'piece'
): { min: number; max: number } | undefined {
  const recipeData = getRecipeImageData(fishId, recipe);
  if (!recipeData) return undefined;
  
  const portionData = recipeData.portionTypes[portionType];
  if (!portionData) return undefined;
  
  return {
    min: portionData.weight_min,
    max: portionData.weight_max
  };
}

/**
 * Get image path for a specific portion/piece
 */
export function getImagePath(
  fishId: string, 
  serialNumber: number,
  recipe: string, 
  portionType: 'whole' | 'portion' | 'piece',
  weight: number,
  pieceType?: string
): string {
  const paddedSerial = serialNumber.toString().padStart(2, '0');
  const ext = RECIPE_IMAGE_CONFIG.FILE_EXTENSION;
  const baseUrl = `${RECIPE_IMAGE_CONFIG.FULL_PATH}/${paddedSerial}-${fishId}/${recipe}`;
  
  if (portionType === 'piece' && pieceType) {
    return `${baseUrl}/piece_${pieceType}-[${weight}].${ext}`;
  } else {
    return `${baseUrl}/${portionType}-[${weight}].${ext}`;
  }
}

/**
 * Get serial number for a fish ID
 */
export function getFishSerialNumber(fishId: string): number | undefined {
  const fishData = getFishRecipeImageData(fishId);
  return fishData?.serialNumber;
}
